import { List } from 'immutable'
import * as _ from "lodash"
import { AppContextProvider, searchInString } from "./AppContext"

import * as api from "../api"
import * as Model from "../models"

export interface IAddressContext {
  // Offline Data
  offlineDatas: List<Model.IAddress>
  
  // Fetching
  isFetching: boolean // UI indication
  lastFetchedPage: number // Last page fetched, "currentPage" in previous version
  hasMore: boolean // Indicate if there is any new page
  fetchErrorMessage: string
  
  // Create
  createStatus: {
    data: Model.IAddress
    isFormOpen: boolean
    isSubmitting: boolean
    errorMessage: string
  }

  // Update
  updateStatuses: {
    [id: string]: {
      id: string
      isSubmitting: boolean
      errorMessage: string
    }
  }
}

export const EmptyAddressContext = (): IAddressContext => {
  /* tslint:disable:object-literal-sort-keys */
  return {
    offlineDatas: List<Model.IAddress>(),
    
    // Fetching
    fetchErrorMessage: "",
    hasMore: true,
    isFetching: false,
    lastFetchedPage: -1,
  
    // Create
    createStatus: {
      data: Model.EmptyAddress(),
      errorMessage: "",
      isFormOpen: false,
      isSubmitting: false
    },
  
    // Update
    updateStatuses: {}
  }
  /* tslint:enable:object-literal-sort-keys */
}

export interface IAddressAction {
  getByAddressId: (addressId: string, searchKeyword?: string) => Model.IAddress
  getByCustomerId: (customerid: string, shippingmethod: Model.ShippingMethodKeys, searchKeyword?: string) => List<Model.IAddress>
  getOfflineDatas: (searchKeywod?: string) => List<Model.IAddress>

  closeCreateForm: () => void
  closeEditForm: (addressid: string) => void
  createRequest: (address: Model.IAddress) => void
  loadMore: (isFetching: boolean, lastFetchedPage: number, hasMore: boolean) => void
  openCreateForm: () => void
  openEditForm: (addressid: string) => void
  updateRequest: (address: Model.IAddress) => void
}

export const AddressAction = (state: IAddressContext, provider: AppContextProvider): IAddressAction => {
  const getOfflineDatas = (searchKeyword?: string): List<Model.IAddress> => {
    const offlineDatas = state.offlineDatas
    if (!searchKeyword) {
      // No Filter
      return offlineDatas
    }
    
    // With Filter
    return applyFilterByKeyword(offlineDatas, searchKeyword)
  }
  const getByAddressId = (addressId: string, searchKeyword?: string): Model.IAddress => {
    const result = getOfflineDatas(searchKeyword)
  
    return result.find((address: Model.IAddress) => {
      return address.addressid === addressId
    })
  }

  const getByCustomerId = (customerId: string, shippingmethod: Model.ShippingMethodKeys, searchKeyword?: string): List<Model.IAddress> => {
    const result = getOfflineDatas(searchKeyword)
  
    return result.filter((address: Model.IAddress) => {
      return address.customerid === customerId && address.shippingtype === shippingmethod
    }).toList()
  }

  const applyFilterByKeyword = (list: List<Model.IAddress>, searchKeyword: string): List<Model.IAddress> => {
    return list.filter((address: Model.IAddress) => {
      return searchInString(address.addressid, searchKeyword) ||
             searchInString(address.name, searchKeyword)
    }).toList()
  }

  const closeCreateForm = () => {
    dispatch(provider, contextHandler(state).closeCreateForm())
  }

  const closeEditForm = (addressid: string) => {
    dispatch(provider, contextHandler(state).closeEditForm(addressid))
  }

  const createRequest = async (address: Model.IAddress) => {
    // Request Start
    dispatch(provider, contextHandler(state).createRequestStart())

    try {
      const result = await api.addresses.create(address)

      const closeFormState = contextHandler(state).closeCreateForm()
      return dispatch(provider, contextHandler(closeFormState).createRequestSucceed(result))
    } catch (error) {
      return dispatch(provider, contextHandler(state).createRequestFail(error))
    }
  }

  const loadMore = async (isFetching: boolean, lastFetchedPage: number, hasMore: boolean) => {
    if (!isFetching && hasMore) {
      // Rquest Start
      dispatch(provider, contextHandler(state).listRequestStart())

      try {
        // Requesting
        const result = await api.addresses.getPaged(lastFetchedPage+1)

        // Request Succeed
        return dispatch(provider, contextHandler(state).listRequestSucceed(result.addresses, result.pagesize))
      } catch (error) {
        // Request Failed
        return dispatch(provider, contextHandler(state).listRequestFail(error))
      }
    }
  }

  const openCreateForm = () => {
    dispatch(provider, contextHandler(state).openCreateForm())
  }

  const openEditForm = (addressid: string) => {
    dispatch(provider, contextHandler(state).openEditForm(addressid))
  }

  const updateRequest = async (address: Model.IAddress) => {
    // Request Start
    const { addressid } = address
    dispatch(provider, contextHandler(state).updateRequestStart(addressid))

    try {
      const resultAddress = await api.addresses.update(address)

      const closeFormState = contextHandler(state).closeEditForm(addressid)
      return dispatch(provider, contextHandler(closeFormState).updateRequestSucceed(resultAddress))
    } catch (error) {
      return dispatch(provider, contextHandler(state).updateRequestFail(error, addressid))
    }
  }

  return {
    getByAddressId,
    getByCustomerId,
    getOfflineDatas,

    closeCreateForm,
    closeEditForm,
    createRequest,
    loadMore,
    openCreateForm,
    openEditForm,
    updateRequest,
  }
}

const dispatch = (provider: AppContextProvider, newState: IAddressContext) => {
  provider.setState({
    address: newState
  })
}

const contextHandler = (previousState: IAddressContext) => {
  const listRequestStart = () => {
    return {
      ...previousState,
      isFetching: true
    }
  }

  const listRequestSucceed = (addresses: Model.IAddress[], pagesize: number) => {
    let hasMore = true
    let currentPage = previousState.lastFetchedPage
  
    if (addresses.length === 0) {
      hasMore = false
    } else if (addresses.length > 0) {
      currentPage++
    }
  
    if (addresses.length < pagesize) {
      hasMore = false
    }
  
    const unexistDatas: Model.IAddress[] = _.filter(addresses, (address: Model.IAddress) => {
      return previousState.offlineDatas.findIndex((item: Model.IAddress) => item.addressid === address.addressid) < 0
    })
    const offlineDatas = previousState.offlineDatas.concat(List(unexistDatas)).toList()
  
    return {
      ...previousState,
      fetchErrorMessage: '',
      hasMore,
      isFetching: false,
      lastFetchedPage: currentPage,
      offlineDatas
    }
  }

  const listRequestFail = (error: Model.IApiErrorResult) => {
    return {
      ...previousState,
      fetchErrorMessage: error.data.message,
      isFetching: false
    }
  }

  const openCreateForm = () => {
    const createStatus = {
      ...previousState.createStatus,
      createAddress: Model.EmptyAddress(),
      isFormOpen: true
    }

    return {
      ...previousState,
      createStatus
    }
  }

  const closeCreateForm = () => {
    const createStatus = {
      ...previousState.createStatus,
      isFormOpen: false
    }

    return {
      ...previousState,
      createStatus
    }
  }

  const createRequestStart = () => {
    const createStatus = {
      ...previousState.createStatus,
      isSubmitting: true
    }

    return {
      ...previousState,
      createStatus
    }
  }

  const createRequestSucceed = (address: Model.IAddress) => {
    const createStatus = {
      ...previousState.createStatus,
      errorMessage: "",
      isSubmitting: false
    }

    const offlineDatas = List([address]).concat(previousState.offlineDatas).toList()

    return {
      ...previousState,
      createStatus,
      offlineDatas
    }
  }

  const createRequestFail = (error: Model.IApiErrorResult) => {
    const createStatus = {
      ...previousState.createStatus,
      errorMessage: error.data.message,
      isSubmitting: false
    }
    
    return {
      ...previousState,
      createStatus
    }
  }

  const openEditForm = (addressid: string) => {
    const updateStatuses = { ...previousState.updateStatuses }
    updateStatuses[addressid] = {
      errorMessage: "",
      id: addressid,
      isSubmitting: false
    }

    return {
      ...previousState,
      updateStatuses
    }
  }

  const closeEditForm = (addressid: string) => {
    const updateStatuses = { ...previousState.updateStatuses}
    delete updateStatuses[addressid]
    return {
      ...previousState,
      updateStatuses
    }
  }

  const updateRequestStart = (addressid: string) => {
    const updateStatuses = { ...previousState.updateStatuses }
    updateStatuses[addressid] = {
      ...updateStatuses[addressid],
      isSubmitting: true
    }

    return {
      ...previousState,
      updateStatuses
    }
  }

  const updateRequestSucceed = (address: Model.IAddress) => {
    const updateStatuses = { ...previousState.updateStatuses }
    delete updateStatuses[address.addressid]

    const offlineDatas = previousState.offlineDatas.asMutable().map((eachItem: Model.IAddress) => {
      if (eachItem.addressid === address.addressid) {
        return address
      }
      return eachItem
    }).toList()

    return {
      ...previousState,
      offlineDatas,
      updateStatuses
    }
  }

  const updateRequestFail = (error: Model.IApiErrorResult, addressid: string) => {
    const updateStatuses = { ...previousState.updateStatuses }
    updateStatuses[addressid] = {
      ...updateStatuses[addressid],
      errorMessage: error.data.message,
      isSubmitting: false
    }

    return {
      ...previousState,
      updateStatuses
    }
  }

  const searchByKeyword = (keyword: string) => {
    return {
      ...previousState,
      searchKeyword: keyword
    }
  }

  return {
    closeCreateForm,
    closeEditForm,
    createRequestFail,
    createRequestStart,
    createRequestSucceed,
    listRequestFail,
    listRequestStart,
    listRequestSucceed,
    openCreateForm,
    openEditForm,
    searchByKeyword,
    updateRequestFail,
    updateRequestStart,
    updateRequestSucceed,
  }
}